import type React from 'react';

import { Button } from '@change/ds-core-ui';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { TypedTranslate } from '@change-corgi/core/react/i18n';
import type { PersonalizedTasksStatus } from '@change-corgi/middle-end-gql-schema';

import { DynamicTask } from './components/DynamicTask';
import { DynamicTasksGenerationInProgress } from './components/DynamicTasksGenerationInProgress';
import { DynamicTasksLoading } from './components/DynamicTasksLoading';
import { SectionHeader } from './components/SectionHeader';
import { PERSONALIZED_TASKS_GENERATION_STATUSES } from './constants';
import { useDynamicTasks } from './hooks/useDynamicTasks';
import { useSyncedCtaWidth } from './hooks/useSyncedCtaWidth';
import { useTaskExpansion } from './hooks/useTaskExpansion';
import type { DynamicTaskShape } from './types';

export const DynamicTasks: React.FC = () => {
	const { data, actions } = useDynamicTasks();
	const {
		tasks,
		stats,
		generationStatus,
		taskListIsExpanded,
		showTaskListToggle,
		toggleTaskListLabel,
		toggleTaskListRemainingCount,
		toggleTaskListIcon,
	} = data;

	const { toggleTaskList } = actions;

	const { isExpanded, toggleExpansion } = useTaskExpansion(0);

	// TODO: REMOVE this stats.count check once the server is updating the generationStatus correctly for users with no tasks.
	// This is a temporary fix to prevent showing an empty state with a "View More" button for users with no tasks.
	if (stats.count <= 0) {
		if (
			generationStatus === PERSONALIZED_TASKS_GENERATION_STATUSES.PENDING ||
			(generationStatus === PERSONALIZED_TASKS_GENERATION_STATUSES.IN_PROGRESS && stats.count === 0)
		) {
			return <DynamicTasksLoading />;
		}
	}

	if (generationStatus === PERSONALIZED_TASKS_GENERATION_STATUSES.FAILED || stats.count === 0) {
		return null;
	}

	return (
		<DynamicTasksMain
			tasks={tasks}
			stats={stats}
			generationStatus={generationStatus}
			taskListIsExpanded={taskListIsExpanded}
			showTaskListToggle={showTaskListToggle}
			toggleTaskListLabel={toggleTaskListLabel}
			toggleTaskListRemainingCount={toggleTaskListRemainingCount}
			toggleTaskListIcon={toggleTaskListIcon}
			toggleTaskList={toggleTaskList}
			isExpanded={isExpanded}
			toggleExpansion={toggleExpansion}
		/>
	);
};

const DynamicTasksMain: React.FC<DynamicTasksMainProps> = ({
	tasks,
	stats,
	generationStatus,
	taskListIsExpanded,
	showTaskListToggle,
	toggleTaskListLabel,
	toggleTaskListRemainingCount,
	toggleTaskListIcon,
	toggleTaskList,
	isExpanded,
	toggleExpansion,
}) => {
	const containerRef = useSyncedCtaWidth(tasks);

	const showGenerationInProgressFooter =
		stats.count >= 1 && generationStatus === PERSONALIZED_TASKS_GENERATION_STATUSES.IN_PROGRESS && taskListIsExpanded;

	const toggleListReplacements =
		toggleTaskListRemainingCount > 0 ? { count: String(toggleTaskListRemainingCount) } : undefined;

	return (
		<div className="bg-background-base flex flex-col gap-5 px-4 py-8 md:p-12" data-qa="dynamic-tasks">
			<SectionHeader completed={stats.completed} total={stats.count} />
			<div className="flex flex-col gap-8">
				<div className="flex flex-col gap-4">
					<div className="flex flex-col" ref={containerRef}>
						{tasks.map((task) => (
							<DynamicTask
								key={task.index}
								task={task}
								isExpanded={isExpanded(task.index)}
								onToggleExpand={() => toggleExpansion(task.index)}
							/>
						))}
					</div>
					{showTaskListToggle && (
						<Button
							variant="tertiary"
							size="x-small"
							onClick={toggleTaskList}
							data-qa="dynamic-tasks-view-more-button"
							className="bg-fill-transparent text-typography-strong text-size-body-small w-full gap-2"
						>
							<TypedTranslate value={toggleTaskListLabel} replacements={toggleListReplacements} />
							{toggleTaskListIcon}
						</Button>
					)}
				</div>
				{showGenerationInProgressFooter && <DynamicTasksGenerationInProgress />}
			</div>
		</div>
	);
};

type DynamicTasksMainProps = Readonly<{
	tasks: readonly DynamicTaskShape[];
	stats: Readonly<{ completed: number; count: number }>;
	generationStatus: PersonalizedTasksStatus;
	taskListIsExpanded: boolean;
	showTaskListToggle: boolean;
	toggleTaskListLabel: TranslationKey;
	toggleTaskListRemainingCount: number;
	toggleTaskListIcon: React.ReactNode;
	toggleTaskList: () => void;
	isExpanded: (index: number) => boolean;
	toggleExpansion: (index: number) => void;
}>;
